import { ColDef, RowClickedEvent, ColumnState, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { KSK2101_R01Dto, defaultKSK2101_R01Dto } from "./Dto_KSK2101_R01";
import { useKSK2101_R01Store } from "./Store_KSK2101_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

import { formatNumber } from "src/utils/number/numberUtils";
import useUiuxStore from "src/store/useUiuxStore";
import { getMenuComponent } from "src/utils/menu/menuUtil";
import { CommonCodeColumn, CompCdColumn, WorkPlaceColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

const COLUMN_STATE_KEY = 'KSK2101_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<KSK2101_R01Dto> | null>;
}

export const Table_KSK2101_R01 = (props: Props) => {

    const { gridRef } = props;

    const { 
        KSK2101_R01List,
        selectedKSK2101_R01List, setSelectedKSK2101_R01List, addSelectedKSK2101_R01List, removeSelectedKSK2101_R01List, initSelectedKSK2101_R01List,
        
        columnState, setColumnState,
    } = useKSK2101_R01Store();

    const { 
        jobTeamList,
        ordTypeList,
        unitList,
        compCdList, 
        employeeCdList,
        workPlaceList,
        workProcessList,
    } = useCommonCodeStore();
	const { addTabList, setActiveTab, setProductWorkNavigationData } = useUiuxStore();

    const codeMaps = useMemo(() => {
        return {
            ordType: new Map(ordTypeList.map(v => [v.minorCd, v.codeNm])),
            workPlace: new Map(workPlaceList.map(v => [v.workplaceCd, v.workplaceNm])),
            unit: new Map(unitList.map(v => [v.minorCd, v.codeNm])),
            emp: new Map(employeeCdList.map(v => [v.empNo, v.empNm])),
            comp: new Map(compCdList.map(v => [v.compCd, v.compNm])),
        };
    }, [ordTypeList, workPlaceList, unitList, employeeCdList, compCdList]);

    const {
        createRowClassRules,
        ensureSelectedRow,
    } = useGridSettingContent<KSK2101_R01Dto>({
        gridRef,
        key: 'valueIndex',
        gridData: KSK2101_R01List || [],
        defaultGridData: defaultKSK2101_R01Dto,
        selectedRow: selectedKSK2101_R01List,
    });
    const getRowIdForGrid = useCallback((params: any) => {
        const id = params.data?.valueIndex || params.id;
        return String(id);
    }, []);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const workPlaceListToUse = useMemo(() => {
		return workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y');
	}, [workPlaceList]);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        
    };

    const getCellStyle = (params: any, key: string) => {
        return onlyTouchGridStyle(
            'valueIndex',
            'productWorkNo',
        )(params);
    }

    const baseColumnDefs: ColDef<KSK2101_R01Dto>[] = useMemo(() => {
        const result: ColDef<KSK2101_R01Dto>[] = [
            ...getDefaultRowIndexColumnConfig(),
			{
				headerName: '공정배정No', colId: 'productWorkNo', field: 'productWorkNo', width: 150, 
				cellStyle: (params: any) => getCellStyle(params, 'productWorkNo'),
				editable: false,
			},
			{
				headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
				cellStyle: (params: any) => getCellStyle(params, 'compCd'),
				...CompCdColumn.options(compCdList, false, undefined, undefined),
			},
			{
				headerName: '작업일자', colId: 'workDt', field: 'workDt', width: 100, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'workDt'),
				valueFormatter: (params: any) => params.data?.workDt ? formatDate(new Date(params.data?.workDt)) : '',
				comparator: cellDateComparator,
			},
			{
				headerName: '작업공정', colId: 'workplaceCd', field: 'workplaceCd', width: 100, 
				cellStyle: (params: any) => getCellStyle(params, 'workplaceCd'),
				...WorkPlaceColumn.options(
					workPlaceListToUse, false, (params: any) => params.data?.compCd, undefined, undefined
				)
			},
			{
				headerName: '공정유형', colId: 'workProcess', field: 'workProcess', width: 100, 
				cellStyle: (params: any) => getCellStyle(params, 'workProcess'),
				...CommonCodeColumn.options(workProcessList, false, undefined, undefined),
			},
			{
				headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam', width: 100, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'jobTeam'),
				cellEditor: 'agSelectCellEditor', 
				cellEditorParams: { 
					values: jobTeamList.map(opt => String(opt.minorCd)) 
				}, 
				valueFormatter: (params: any) => { 
					const codeNm = jobTeamList.find(opt => String(opt.minorCd) === String(params.value)); 
					return codeNm ? codeNm.codeNm : params.value; 
				}
			},
			{
				headerName: '작업자', colId: 'workEmp', field: 'workEmp', width: 100, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'workEmp'),
			},
			{
				headerName: '작업자명', colId: 'workEmpNm', field: 'workEmpNm', width: 100, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'workEmpNm'),
			},
			{
				headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 150, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'itemCd'),
			},
			{
				headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'itemNm'),
			},
			{
				headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'itemSpec'),
			},
			{
				headerName: '지시량', colId: 'orderQty', field: 'orderQty', width: 100, 
				editable: false,
				cellStyle: (params: any) => getCellStyle(params, 'orderQty'),
				valueFormatter: (params: any) =>
					params.value != null ? params.value.toLocaleString() : '',
			},
			{
				headerName: '공정배정명', colId: 'workprocess', field: 'workprocess', width: 100, 
				editable: false,
				cellRenderer: (params: any) => {
					// 하단 합계 행(pinned row)인 경우 렌더링 하지 않음
					if (params.node.rowPinned) return null;
	
					return (
						<div className="w-full h-full flex items-center justify-center">
							<button
								className="w-full h-full flex items-center justify-center bg-gray-300 text-white text-[18px] rounded hover:bg-gray-600 border border-gray-600"
								onClick={(e) => {
									e.stopPropagation(); // 행 클릭 이벤트 전파 방지
									handleWorkprocessClick({ data: params.data } as RowClickedEvent<KSK2101_R01Dto>);
								}}
							>
								지시
							</button>
						</div>
					);
				}
			},
        ];
        
        return result;
    }, [hoveredRowIndex, KSK2101_R01List, selectedKSK2101_R01List, compCdList, ordTypeList, unitList, employeeCdList, workPlaceList, codeMaps]);

    const columnDefs: ColDef<KSK2101_R01Dto>[] = useMemo(() => {
        const result = GridCustomColumnDefs<KSK2101_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass
        });
        return result;
    }, [baseColumnDefs, columnState, headerNames]);

    const getRowStyleForKSK2101_R01 = useCallback((params: any) => {
        if (params.node.rowPinned === 'bottom') {
            return { background: '#F0F0F0', fontWeight: 'bold', borderTop: 'none' };
        }
        return undefined;
    }, []);

    const handleRowClick = useCallback((e: RowClickedEvent<KSK2101_R01Dto>) => {
        if (e.node.rowPinned === 'bottom') return;
        const rowData = e.data;
        if (!rowData) return;
        
        // const compositeKey = getCompositeKey(rowData);
        const isSelected = selectedKSK2101_R01List.includes(rowData.productWorkNo);
        
        if (isSelected) {
            removeSelectedKSK2101_R01List([rowData.productWorkNo]);
        } else {
            setSelectedKSK2101_R01List([rowData.productWorkNo]);
        }
    }, [selectedKSK2101_R01List, addSelectedKSK2101_R01List, removeSelectedKSK2101_R01List]);

	const productWorkNavigationProcess = useCallback((productWorkNo: string) => {
		// 네비게이션 데이터 설정 (공정배정번호만 전달)
		setProductWorkNavigationData({
			productWorkNo: productWorkNo
		});

		// // KSK2101_R01 탭 추가
		const ksk2021Component = getMenuComponent('KSK2021_R01');
		if (ksk2021Component) {
			const newTab = {
				key: 'KSK2021_R01',
				title: '생산실적 등록(PIC)',
				component: ksk2021Component
			};
			
			addTabList(newTab);
			setActiveTab(newTab);
		}
	}, [setProductWorkNavigationData, addTabList, setActiveTab]);


	const handleWorkprocessClick = useCallback((e: RowClickedEvent<KSK2101_R01Dto>) => {
		const rowData = e.data;
		if (!rowData) return;

		productWorkNavigationProcess(rowData.productWorkNo);
	}, [productWorkNavigationProcess]);

	
	// 더블클릭으로 KSK2021_R01 페이지로 이동
	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<KSK2101_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		productWorkNavigationProcess(rowData.productWorkNo);
	}, [setProductWorkNavigationData, addTabList, setActiveTab]);

    const pinnedBottomRow = useMemo(() => {
        const detailRows = KSK2101_R01List;
        const totals = detailRows.reduce((acc, item) => {
            return {
                editable: false,
            };
        }, { editable: false });

        return [{
            rowIndex: '',
            productWorkNo: detailRows.length.toLocaleString() + "건",
            editable: false,
        }];
    }, [KSK2101_R01List]);

    useEffect(() => { ensureSelectedRow(); }, [KSK2101_R01List, ]);

    // 복합키 기반 rowClassRules
    const rowClassRules = useMemo(() => ({
        'selected': (params: any) => {
            if (!params.data) return false;
            // const compositeKey = params.data.valueIndex;
            return selectedKSK2101_R01List.includes(params.data.productWorkNo);
        }
    }), [selectedKSK2101_R01List]);

    const handleRowDragEnd = useCallback((e: any) => {
        const api = gridRef.current?.api || e?.api;
        if (!api) return;
        
        if (api.applyColumnState) {
            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
        }
        
        const reordered: KSK2101_R01Dto[] = [];
        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
            if (!node?.data) return;
            reordered.push({
                ...node.data,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
            } as KSK2101_R01Dto);
        });
        // 드래그 후 리스트 자체는 유지 (필요시 상태 업데이트 로직 추가)
    }, [gridRef]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<KSK2101_R01Dto>
                gridRef={gridRef}
                paddedGridData={KSK2101_R01List}
                initSelected={initSelectedKSK2101_R01List}
				defaultRowData={defaultKSK2101_R01Dto}
                baseColumnDefs={columnDefs}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                enableRowDrag={true}
                onAfterReorder={handleRowDragEnd}
            >
                <Grid_Select<KSK2101_R01Dto>
                    ref={gridRef}
                    rowData={KSK2101_R01List}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowIdForGrid}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
                    onGridReady={(params) => {
                        params.api.getDisplayedRowCount();
                        params.api.getRenderedNodes();
                    }}
                    onRowClicked={handleRowClick}
					onRowDoubleClicked={handleRowDoubleClick}
                    pinnedBottomRowData={pinnedBottomRow}
                    rowClassRules={rowClassRules}
                    getRowStyle={getRowStyleForKSK2101_R01}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
                    onRowDragEnd={handleRowDragEnd}
                    isRowSelectable={(node) => !!node.data}
                    suppressRowClickSelection={false}
                    ensureDomOrder={true}
                />
            </GridContextWrapper>
        </div>
    );
}


